import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import AssignmentTurnedInIcon from '@material-ui/icons/AssignmentTurnedIn';
import ChatIcon from '@material-ui/icons/Chat';
import CloseIcon from '@material-ui/icons/Close';
import ErrorIcon from '@material-ui/icons/Error';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import SearchIcon from '@material-ui/icons/Search';
import VisibilityIcon from '@material-ui/icons/Visibility';
import RateReviewIcon from '@material-ui/icons/RateReview';
import { useKnowledgeBuilder } from './KnowledgeBuilderProvider';
import { KnowledgeChat } from './KnowledgeChat';
import { DocumentTree } from './DocumentTree';
import { MonacoDocumentEditor } from './MonacoDocumentEditor';
import { MarkdownPreview } from './MarkdownPreview';
import { ProposedChangeReview } from './ProposedChangeReview';
import { ReviewPanel } from './ReviewPanel';
import {
  KnowledgeBuilderColors,
  useKnowledgeBuilderColors,
} from '../../theme/knowledgeBuilderColors';

type WorkspaceTab =
  | 'editor'
  | 'preview'
  | 'proposals'
  | 'validation'
  | 'review';

export const KnowledgeBuilderLayout = () => {
  const {
    activeDocument,
    activeDocumentPath,
    capabilities,
    dirtyPaths,
    documents,
    error,
    progress,
    proposedChanges,
    saveState,
    session,
    dismissError,
    selectDocument,
    validateSession,
  } = useKnowledgeBuilder();
  const [tab, setTab] = useState<WorkspaceTab>('editor');
  const [chatWidth, setChatWidth] = useState(300);
  const [treeWidth, setTreeWidth] = useState(260);
  const [searchQuery, setSearchQuery] = useState('');
  const [openPaths, setOpenPaths] = useState<string[]>([]);
  const lastPendingProposalCount = useRef(0);
  const colors = useKnowledgeBuilderColors();
  const styles = useMemo(() => buildStyles(colors), [colors]);
  const pendingProposals = proposedChanges.filter(
    change => change.status === 'pending',
  );

  useEffect(() => {
    if (!activeDocumentPath) {
      return;
    }
    setOpenPaths(current =>
      current.includes(activeDocumentPath)
        ? current
        : [...current, activeDocumentPath],
    );
  }, [activeDocumentPath]);

  useEffect(() => {
    if (pendingProposals.length > lastPendingProposalCount.current) {
      setTab('proposals');
    }
    lastPendingProposalCount.current = pendingProposals.length;
  }, [pendingProposals.length]);

  const openDocuments = openPaths
    .map(path => documents.find(document => document.path === path))
    .filter(Boolean) as typeof documents;

  const startChatResize = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    const startX = event.clientX;
    const startWidth = chatWidth;
    const onMouseMove = (moveEvent: MouseEvent) => {
      setChatWidth(clamp(startWidth - (moveEvent.clientX - startX), 260, 520));
    };
    const onMouseUp = () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };

  const startTreeResize = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    const startX = event.clientX;
    const startWidth = treeWidth;
    const onMouseMove = (moveEvent: MouseEvent) => {
      setTreeWidth(clamp(startWidth + (moveEvent.clientX - startX), 180, 520));
    };
    const onMouseUp = () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };

  const closeOpenPath = (path: string) => {
    setOpenPaths(current => {
      const next = current.filter(item => item !== path);
      if (path === activeDocumentPath) {
        const nextActive = next[next.length - 1];
        if (nextActive) {
          selectDocument(nextActive);
        }
      }
      return next;
    });
  };

  return (
    <div className="knowledge-builder-rounded" style={styles.root}>
      <div style={styles.commandBar}>
        <div />
        <label style={styles.searchBar}>
          <SearchIcon style={{ fontSize: 15, color: colors.text.secondary }} />
          <input
            value={searchQuery}
            onChange={event => setSearchQuery(event.target.value)}
            placeholder="Search in current workspace..."
            style={styles.searchInput}
          />
        </label>
        <div style={styles.topActions}>
          {error && <ErrorIcon style={{ color: '#f87171', fontSize: 17 }} />}
          {progress && <span style={styles.activityDot} />}
          <Tooltip title="Validate">
            <IconButton
              size="small"
              onClick={validateSession}
              style={{ color: colors.text.secondary, padding: 4 }}
            >
              <AssignmentTurnedInIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
      </div>
      {error && (
        <div style={styles.error}>
          <Typography variant="body2" style={{ fontSize: 12 }}>
            {error}
          </Typography>
          <IconButton
            size="small"
            aria-label="Dismiss error"
            onClick={dismissError}
            style={{ color: colors.status.errorText }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
      )}
      <div style={styles.body}>
        <div
          className="knowledge-builder-scrollbar"
          style={{
            width: treeWidth,
            flex: `0 0 ${treeWidth}px`,
            minWidth: 180,
            maxWidth: 520,
            display: 'flex',
            minHeight: 0,
          }}
        >
          <DocumentTree
            selectedPath={
              isActiveDocumentOpen(activeDocumentPath, openPaths)
                ? activeDocumentPath
                : undefined
            }
            searchQuery={searchQuery}
          />
        </div>
        <div
          onMouseDown={startTreeResize}
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize explorer"
          style={styles.resizeHandle}
        >
          <ResizeGrip />
        </div>
        <div style={styles.main}>
          <div style={styles.workspace}>
            <div style={styles.editorColumn}>
              <div style={styles.tabBar}>
                <div style={styles.fileTabs}>
                  {openDocuments.map(document => (
                    <button
                      key={document.path}
                      type="button"
                      onClick={() => {
                        selectDocument(document.path);
                        setTab('editor');
                      }}
                      style={fileTabStyle(
                        colors,
                        document.path === activeDocumentPath,
                      )}
                    >
                      <InsertDriveFileIcon style={{ fontSize: 13 }} />
                      <span style={styles.fileTabText}>
                        {document.path.split('/').pop()}
                      </span>
                      {dirtyPaths.includes(document.path) && (
                        <span style={styles.dirtyDot} />
                      )}
                      <span
                        role="button"
                        tabIndex={0}
                        aria-label={`Close ${document.path}`}
                        onClick={event => {
                          event.preventDefault();
                          event.stopPropagation();
                          closeOpenPath(document.path);
                        }}
                        onKeyDown={event => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            event.stopPropagation();
                            closeOpenPath(document.path);
                          }
                        }}
                        style={styles.closeTab}
                      >
                        x
                      </span>
                    </button>
                  ))}
                </div>
                <div style={styles.modeGroup}>
                  <ModeButton
                    colors={colors}
                    active={tab === 'editor'}
                    title="Editor"
                    onClick={() => setTab('editor')}
                  >
                    <InsertDriveFileIcon fontSize="small" />
                  </ModeButton>
                  <ModeButton
                    colors={colors}
                    active={tab === 'preview'}
                    title="Preview"
                    onClick={() => setTab('preview')}
                  >
                    <VisibilityIcon fontSize="small" />
                  </ModeButton>
                  <ModeButton
                    colors={colors}
                    active={tab === 'proposals'}
                    title={
                      pendingProposals.length
                        ? `AI changes (${pendingProposals.length})`
                        : 'AI changes'
                    }
                    onClick={() => setTab('proposals')}
                  >
                    <ChatIcon fontSize="small" />
                  </ModeButton>
                  <ModeButton
                    colors={colors}
                    active={tab === 'validation'}
                    title="Validation"
                    onClick={() => setTab('validation')}
                  >
                    <AssignmentTurnedInIcon fontSize="small" />
                  </ModeButton>
                  <ModeButton
                    colors={colors}
                    active={tab === 'review'}
                    title="Review generated output"
                    onClick={() => setTab('review')}
                  >
                    <RateReviewIcon fontSize="small" />
                  </ModeButton>
                </div>
              </div>
              <div style={styles.content}>
                {!isActiveDocumentOpen(activeDocumentPath, openPaths) &&
                  tab !== 'proposals' &&
                  tab !== 'validation' &&
                  tab !== 'review' && (
                    <Typography
                      style={{
                        padding: 16,
                        color: colors.text.muted,
                        fontSize: 12,
                      }}
                    >
                      No document selected.
                    </Typography>
                  )}
                {activeDocument &&
                  isActiveDocumentOpen(activeDocumentPath, openPaths) &&
                  tab === 'preview' && (
                    <MarkdownPreview document={activeDocument} />
                  )}
                {activeDocument &&
                  isActiveDocumentOpen(activeDocumentPath, openPaths) &&
                  tab === 'editor' && (
                    <MonacoDocumentEditor document={activeDocument} />
                  )}
                {tab === 'proposals' && <ProposedChangeReview />}
                {tab === 'review' && <ReviewPanel />}
              </div>
            </div>
            <div
              onMouseDown={startChatResize}
              role="separator"
              aria-orientation="vertical"
              aria-label="Resize conversation panel"
              style={styles.resizeHandle}
            >
              <ResizeGrip />
            </div>
            <div
              className="knowledge-builder-scrollbar"
              style={{
                width: chatWidth,
                flex: `0 0 ${chatWidth}px`,
                display: 'flex',
                minHeight: 0,
              }}
            >
              <KnowledgeChat />
            </div>
          </div>
        </div>
      </div>
      <div style={styles.statusBar}>
        <span>
          {isActiveDocumentOpen(activeDocumentPath, openPaths) && activeDocument
            ? activeDocument.path
            : 'No file selected'}
        </span>
        <span>
          {documents.length} files | {capabilities?.routes.chat ?? 'offline'} |{' '}
          {saveState} | draft {session?.id ?? 'pending'}
        </span>
      </div>
      <style>
        {`
          .knowledge-builder-scrollbar,
          .knowledge-builder-scrollbar * {
            scrollbar-width: thin;
            scrollbar-color: ${colors.scrollbar.thumb} ${colors.scrollbar.track};
          }
          .knowledge-builder-scrollbar::-webkit-scrollbar,
          .knowledge-builder-scrollbar *::-webkit-scrollbar {
            width: 12px;
            height: 12px;
          }
          .knowledge-builder-scrollbar::-webkit-scrollbar-track,
          .knowledge-builder-scrollbar *::-webkit-scrollbar-track {
            background: ${colors.scrollbar.track};
          }
          .knowledge-builder-scrollbar::-webkit-scrollbar-thumb,
          .knowledge-builder-scrollbar *::-webkit-scrollbar-thumb {
            background-color: ${colors.scrollbar.thumb};
            border-radius: 6px;
            border: 3px solid ${colors.scrollbar.track};
          }
          .knowledge-builder-scrollbar::-webkit-scrollbar-thumb:hover,
          .knowledge-builder-scrollbar *::-webkit-scrollbar-thumb:hover {
            background-color: ${colors.scrollbar.thumbHover};
          }
          .knowledge-builder-scrollbar-none {
            scrollbar-width: none;
          }
          .knowledge-builder-scrollbar-none::-webkit-scrollbar {
            width: 0;
            height: 0;
          }
          .knowledge-builder-rounded .MuiOutlinedInput-root,
          .knowledge-builder-rounded .MuiOutlinedInput-notchedOutline {
            border-radius: 14px;
          }
          .knowledge-builder-chat-composer .MuiOutlinedInput-notchedOutline {
            border: 0;
          }
          .knowledge-builder-resize-dot {
            width: 2px;
            height: 2px;
            border-radius: 50%;
            background: ${colors.text.secondary};
            display: block;
          }
        `}
      </style>
    </div>
  );
};

function ModeButton(options: {
  colors: KnowledgeBuilderColors;
  active: boolean;
  disabled?: boolean;
  title: string;
  children: ReactNode;
  onClick(): void;
}) {
  return (
    <Tooltip title={options.title}>
      <span>
        <IconButton
          size="small"
          disabled={options.disabled}
          onClick={options.onClick}
          style={modeButtonStyle(options.colors, options.active)}
        >
          {options.children}
        </IconButton>
      </span>
    </Tooltip>
  );
}

function ResizeGrip() {
  return (
    <span style={resizeGripStyle}>
      <span className="knowledge-builder-resize-dot" />
      <span className="knowledge-builder-resize-dot" />
      <span className="knowledge-builder-resize-dot" />
    </span>
  );
}

function buildStyles(colors: KnowledgeBuilderColors) {
  return {
    root: {
      border: `1px solid ${colors.border.default}`,
      borderRadius: 12,
      minHeight: 'calc(100vh - 210px)',
      height: 'min(860px, calc(100vh - 210px))',
      display: 'flex',
      flexDirection: 'column' as const,
      background: colors.bg.root,
      color: colors.text.primary,
      overflow: 'hidden',
      fontSize: 12,
      fontFamily:
        '"Segoe UI", -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif',
    },
    commandBar: {
      minHeight: 34,
      display: 'grid',
      gridTemplateColumns:
        'minmax(190px, 1fr) minmax(260px, 520px) minmax(220px, 1fr)',
      alignItems: 'center',
      gap: 8,
      padding: '0 8px',
      borderBottom: `1px solid ${colors.bg.panel}`,
      background: colors.bg.panelAlt,
    },
    topActions: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: 4,
    },
    searchBar: {
      height: 24,
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      border: `1px solid ${colors.border.strong}`,
      borderRadius: 12,
      background: colors.bg.input,
      color: colors.text.secondary,
      padding: '0 8px',
      boxSizing: 'border-box' as const,
    },
    searchInput: {
      flex: 1,
      minWidth: 0,
      border: 0,
      outline: 0,
      background: 'transparent',
      color: colors.text.primary,
      fontSize: 12,
      fontFamily:
        '"Segoe UI", -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif',
    },
    body: {
      display: 'flex',
      flex: 1,
      minHeight: 0,
    },
    main: {
      flex: 1,
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column' as const,
    },
    workspace: {
      display: 'flex',
      minHeight: 0,
      flex: 1,
    },
    editorColumn: {
      flex: 1,
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column' as const,
    },
    tabBar: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      minHeight: 30,
      borderBottom: `1px solid ${colors.bg.panel}`,
      background: colors.bg.panel,
    },
    fileTabs: {
      display: 'flex',
      alignItems: 'stretch',
      minWidth: 0,
      alignSelf: 'stretch',
      overflow: 'hidden',
    },
    modeGroup: {
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      flexShrink: 0,
      paddingRight: 4,
    },
    content: {
      flex: 1,
      minHeight: 0,
      overflow: 'hidden',
      background: colors.bg.root,
    },
    resizeHandle: {
      width: 5,
      flex: '0 0 5px',
      cursor: 'col-resize',
      background: colors.bg.panel,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    statusBar: {
      minHeight: 20,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 12,
      padding: '0 8px',
      background: colors.accent.statusBar,
      color: '#f8fafc',
      fontSize: 11,
    },
    error: {
      padding: '6px 10px',
      borderBottom: `1px solid ${colors.status.errorBorder}`,
      background: colors.status.errorBg,
      color: colors.status.errorText,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 12,
    },
    activityDot: {
      width: 7,
      height: 7,
      borderRadius: '50%',
      background: '#60a5fa',
      display: 'inline-block' as const,
    },
    fileTabText: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap' as const,
      maxWidth: 160,
    },
    dirtyDot: {
      width: 7,
      height: 7,
      borderRadius: '50%',
      background: colors.text.primary,
      flex: '0 0 7px',
    },
    closeTab: {
      color: colors.text.secondary,
      fontSize: 12,
      lineHeight: 1,
      padding: '2px 4px',
      cursor: 'pointer',
    },
  };
}

const resizeGripStyle = {
  display: 'flex',
  flexDirection: 'column' as const,
  gap: 2,
  opacity: 0.65,
};

function fileTabStyle(colors: KnowledgeBuilderColors, active: boolean) {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    minWidth: 120,
    maxWidth: 220,
    height: 30,
    padding: '0 8px',
    borderLeft: active
      ? `1px solid ${colors.bg.input}`
      : '1px solid transparent',
    borderBottom: active
      ? `1px solid ${colors.bg.input}`
      : '1px solid transparent',
    borderRight: `1px solid ${colors.bg.root}`,
    borderTop: active
      ? `1px solid ${colors.accent.statusBar}`
      : '1px solid transparent',
    borderRadius: '8px 8px 0 0',
    background: active ? colors.bg.root : colors.bg.panelAlt,
    color: active ? colors.text.onAccent : colors.text.secondary,
    cursor: 'pointer',
    fontSize: 12,
  };
}

function modeButtonStyle(colors: KnowledgeBuilderColors, active: boolean) {
  return {
    width: 26,
    height: 26,
    padding: 3,
    borderRadius: 8,
    background: active ? colors.bg.hover : 'transparent',
    color: active ? colors.text.onAccent : colors.text.secondary,
  };
}

function isActiveDocumentOpen(
  activeDocumentPath: string | undefined,
  openPaths: string[],
) {
  return Boolean(activeDocumentPath && openPaths.includes(activeDocumentPath));
}

function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(max, value));
}
